import { Component } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";

import getLocalization from "./localizationProvider.js";
import getImage from "../services/getImage.js";
import Feed from "./Feed";
import Score from "./Score";

const ANIMATION_DURATION = 1;

export default class FeedContainer extends Component {
  constructor() {
    super();

    this.state = {
      games: [],
      currentGameIndex: 0,
    };
  }

  componentDidMount() {
    this.setState({
      games: this.dataParser(this.props.items),
    });
    this.recursiveTimer();
  }

  recursiveTimer() {
    const { currentGameIndex, games } = this.state;
    this.setState({
      currentGameIndex:
        currentGameIndex < games.length - 1 ? currentGameIndex + 1 : 0,
    });
    setTimeout(() => {
      this.recursiveTimer();
    }, (this.props.duration + ANIMATION_DURATION * 2) * 1000);
  }

  dataParser(games) {
    const localization = getLocalization(this.props.lang);

    return games.map((game) => {
      const gameDate = this.parseDate(game.DateTime);
      const gamePlace =
        game.VenueDetail ||
        game.StadiumDetails ||
        game.StadiumDetail ||
        game.PlaceDetail;
      return {
        id: game.GameID || game.GameId || game.GameKey,
        teamA: {
          shortName: game.AwayTeamKey || game.AwayTeam,
          fullName:
            game.AwayTeamName ||
            game.AwayTeamDetail.FullName ||
            game.AwayTeamDetail.Name ||
            game.AwayTeamDetail.School,
          logo: game.AwayTeamDetail && game.AwayTeamDetail.WikipediaLogoUrl,
        },
        teamB: {
          shortName: game.HomeTeamKey || game.HomeTeam,
          fullName:
            game.HomeTeamName ||
            game.HomeTeamDetail.FullName ||
            game.HomeTeamDetail.Name ||
            game.HomeTeamDetail.School,
          logo:
            game.HomeTeamDetail &&
            (game.HomeTeamDetail.TeamLogoUrl ||
              game.HomeTeamDetail.WikipediaLogoUrl),
        },
        place: {
          stadium: gamePlace?.Name,
          country: gamePlace?.Country,
          city: gamePlace?.City,
          address: gamePlace?.Address,
        },
        date: {
          weekday: localization.days[gameDate.getDay()],
          month: localization.monthsShort[gameDate.getMonth()],
          date: gameDate.getDate().toString(),
          time: gameDate.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          }),
        },
        score: {
          result: this.parseScore(game),
        },
      };
    });
  }

  parseDate(date) {
    const { sportName } = this.props;
    const isDstObserved = this.isDstObserved();
    const tzOffset = [
      "soccer-uefach",
      "soccer-uefal",
      "soccer-uefaeq",
      "soccer-ech",
      "soccer-fifawc",
      "soccer-fifaf",
      "soccer-epl",
      "soccer-gbl",
      "soccer-spd",
      "soccer-isa",
      "soccer-ned",
      "soccer-acan",
      "soccer-fl1",
      "soccer-rfpl",
      "soccer-ici",
      "soccer-cwc",
    ].includes(sportName)
      ? "+00:00"
      : isDstObserved
      ? "-04:00"
      : "-05:00";

    const dateOutput = new Date(`${date}${tzOffset}`);

    return dateOutput;
  }

  isDstObserved = () => {
    const today = new Date();
    return today.getTimezoneOffset() < this.stdTimezoneOffset(today);
  };

  stdTimezoneOffset(today) {
    const jan = new Date(today.getFullYear(), 0, 1);
    const jul = new Date(today.getFullYear(), 6, 1);
    return Math.max(jan.getTimezoneOffset(), jul.getTimezoneOffset());
  }

  parseScore(gameObj) {
    if (gameObj.Status === "Final" || gameObj.IsOver || gameObj.IsClosed) {
      if (gameObj.AwayTeamScore !== undefined) {
        return `${gameObj.HomeTeamScore}:${gameObj.AwayTeamScore}`;
      } else if (gameObj.AwayTeamRuns !== undefined) {
        return `${gameObj.HomeTeamRuns}:${gameObj.AwayTeamRuns}`;
      } else if (gameObj.AwayTeamHits !== undefined) {
        return `${gameObj.HomeTeamHits}:${gameObj.AwayTeamHits}`;
      } else if (gameObj.AwayScore !== undefined) {
        return `${gameObj.HomeScore}:${gameObj.AwayScore}`;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }

  render() {
    const { fontFamily, textColor, sportName, positionConfig } = this.props;
    const { games, currentGameIndex } = this.state;
    const { hva, hha, htp, ava, aha, atp, sva, stp } = positionConfig;
    const currentGame = games[currentGameIndex];
    const isMLB = sportName === "mlb";
    var portrait = "";
    if (window.innerHeight > window.innerWidth) {
      portrait = "portrait";
    }
    if (currentGame) {
      const { teamA, teamB } = currentGame;
      const hasScore = currentGame.score && currentGame.score.result;
      const leftTeam = isMLB && !hasScore ? teamA : teamB;
      const rightTeam = isMLB && !hasScore ? teamB : teamA;
      return (
        <div
          className={classNames(
            "feed-container",
            `font-${fontFamily}`,
            `text-color-${textColor}`,
            `${portrait}`
          )}
          style={{
            backgroundImage: `url(${getImage[sportName].bg})`,
          }}
        >
          <img
            src={getImage[sportName].logo}
            alt="Logo"
            className="sport-logo"
          />
          <Feed
            id={currentGame.id}
            sportName={sportName}
            shortName={leftTeam.shortName}
            fullName={leftTeam.fullName}
            logo={leftTeam.logo}
            animationDuration={ANIMATION_DURATION}
            verticalAlign={ava}
            horizontalAlign={aha}
            textPosition={atp}
            side={"left"}
          />
          <Score
            id={currentGame.id}
            place={currentGame.place}
            score={currentGame.score}
            date={currentGame.date}
            sportName={sportName}
            animationDuration={ANIMATION_DURATION}
            verticalAlign={sva}
            textPosition={stp}
          />
          <Feed
            id={currentGame.id}
            sportName={sportName}
            shortName={rightTeam.shortName}
            fullName={rightTeam.fullName}
            logo={rightTeam.logo}
            animationDuration={ANIMATION_DURATION}
            verticalAlign={hva}
            horizontalAlign={hha}
            textPosition={htp}
            side={"right"}
          />
        </div>
      );
    } else {
      return "Loading...";
    }
  }
}

const sportTypes = [
  "soccer-epl", // English Premier League
  "soccer-gbl", // Germany Bundesliga
  "soccer-uefach", // UEFA Champions League
  "soccer-spd", // Spanish Primera Division
  "soccer-asa", // Argentina Superliga Argentina
  "soccer-isa", // Italy Serie A
  "soccer-ned", // Netherlands Eredivisie
  "soccer-mls", // United States MLS
  "soccer-uefal", // UEFA Europa League
  "soccer-saca", // South America Copa America
  "soccer-acan", // Africa Cup of Nations
  "soccer-lmx", // Mexico Liga MX
  "soccer-fl1", // France Ligue 1
  "soccer-rfpl", // Russia
  "soccer-bsa", // Brazil Série A
  "soccer-ech", // European Championship
  "soccer-uefaeq", // UEFA Euro Qualification
  "soccer-concacaf", // CONCACAF Gold Cup
  "soccer-aal", // Australia A League
  "soccer-sacl", // South America Copa Libertadores
  "soccer-fifawc", // FIFA World Cup
  "soccer-sacs", // South America Copa Sudamericana
  "soccer-jps", // Costa Rica Campeonato JPS
  "soccer-isl", // Indian Super League
  "soccer-fifaf", // FIFA Friendlies
  "soccer-ici", // Italy Coppa Italia
  "nfl", // NFL
  "nhl", // NHL
  "mlb", // MLB
  "nba", // NBA
  "cfb", // CFB (College Football)
  "cbb", // CBB (College Basketball)
];

FeedContainer.propTypes = {
  sportName: PropTypes.oneOf(sportTypes),
  fontFamily: PropTypes.oneOf(["Bungee", "Lato", "RussoOne", "Roboto"]),
  textColor: PropTypes.oneOf(["light", "dark"]),
  duration: PropTypes.number,
  items: PropTypes.array,
  lang: PropTypes.string,
  positionConfig: PropTypes.object,
};

FeedContainer.defaultProps = {
  sportName: "fifa",
  fontFamily: "Lato",
  textColor: "light",
  duration: "30",
};
